import { cn } from "@app/lib/utils";
import {
  footprintCountries,
  footprintSalesMarkets,
} from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CountryPanel } from "./CountryPanel";
import { FootprintLegend } from "./FootprintLegend";
import { GlobeVisual } from "./GlobeVisual";
import { StarField } from "./StarField";

/**
 * Global Footprint — Section 05 of the approved IR content sheet.
 *
 * Opens with no country selected: the globe sits centred and the detail panel
 * is hidden. Selecting a marker (or a row in the "View all countries" list)
 * opens the panel — on the trailing edge from `lg` up, stacked underneath the
 * globe below that.
 */
export function GlobalFootprint() {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isListOpen, setIsListOpen] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedCountry =
    footprintCountries.find(({ key }) => key === selectedKey) ?? null;

  function selectCountry(key: string) {
    setSelectedKey((current) => (current === key ? null : key));
    setIsListOpen(false);
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;

      setIsListOpen(false);
      setSelectedKey(null);
    }

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;

      if (listRef.current && !listRef.current.contains(target)) {
        setIsListOpen(false);
      }

      if (stageRef.current && !stageRef.current.contains(target)) {
        setSelectedKey(null);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, []);

  // Bring the stacked panel into view on smaller screens, where it renders
  // below the globe rather than beside it.
  useEffect(() => {
    if (!selectedKey || !panelRef.current) return;
    if (window.matchMedia("(min-width: 1024px)").matches) return;

    panelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedKey]);

  return (
    <section
      aria-labelledby="global-footprint-title"
      className="relative overflow-hidden bg-primary py-10 lg:py-12"
    >
      <StarField />

      <div className="container relative">
        <div ref={stageRef} className="relative xl:min-h-[28rem]">
          {/* Heading — inline on small screens, overlaid on the stage from lg up */}
          <div className="relative z-20 xl:absolute xl:inset-y-0 xl:start-0 xl:flex xl:w-60 xl:flex-col 2xl:w-72">
            <h2
              id="global-footprint-title"
              className="text-xl font-bold uppercase leading-tight tracking-wide text-white sm:text-2xl"
            >
              {trans("globalFootprint.title")}
            </h2>

            <p className="mt-2.5 max-w-md text-sm leading-relaxed text-white/60">
              {trans("globalFootprint.intro")}
            </p>

            <div ref={listRef} className="relative mt-5 w-max max-w-full">
              <button
                type="button"
                onClick={() => setIsListOpen((open) => !open)}
                aria-expanded={isListOpen}
                className="inline-flex items-center gap-2 border border-white/40 px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bright-orange"
              >
                {trans("globalFootprint.viewAllCountries")}
                <ChevronDown
                  className={cn(
                    "size-4 transition-transform duration-300",
                    isListOpen && "rotate-180",
                  )}
                  strokeWidth={2}
                  aria-hidden
                />
              </button>

              {isListOpen && (
                <ul className="absolute start-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-white/15 bg-[#040e28] py-1 shadow-2xl">
                  {footprintCountries.map(({ key, name }) => (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => selectCountry(key)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 px-4 py-2.5 text-start text-sm transition-colors hover:bg-white/10",
                          key === selectedKey
                            ? "text-bright-orange"
                            : "text-white/80",
                        )}
                      >
                        {trans(name)}
                        <ChevronRight
                          className="size-4 shrink-0 rtl:rotate-180"
                          strokeWidth={1.5}
                          aria-hidden
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Globe, shown whole — the sphere rotates to face the selected
              country, so nothing about it is decorative any more. When a panel
              opens the globe slides clear of it: a transform, so the sphere
              keeps its size, with explicit per-direction variants so it moves
              away from whichever edge the panel is on. */}
          <div
            className={cn(
              "relative mx-auto mt-6 w-full max-w-[40rem] transition-transform duration-500 xl:mt-0 2xl:max-w-[46rem] 3xl:max-w-[52rem]",
              selectedCountry &&
                "xl:ltr:-translate-x-[14%] xl:rtl:translate-x-[14%]",
            )}
          >
            <GlobeVisual
              countries={footprintCountries}
              salesMarkets={footprintSalesMarkets}
              selectedKey={selectedKey}
              onSelect={selectCountry}
            />
          </div>

          <div className="relative z-10 mt-5 xl:absolute xl:inset-x-0 xl:bottom-0 xl:mt-0">
            <FootprintLegend />
          </div>

          {selectedCountry && (
            <div
              ref={panelRef}
              className="relative z-20 mt-8 xl:absolute xl:inset-y-0 xl:end-0 xl:mt-0 xl:flex xl:w-[22rem] xl:items-center 2xl:w-[26rem]"
            >
              <CountryPanel
                country={selectedCountry}
                countries={footprintCountries}
                onClose={() => setSelectedKey(null)}
                // The pager always lands on a country — unlike the markers, it
                // never toggles the current one back off.
                onSelect={setSelectedKey}
                className="w-full animate-fade"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
